import { useMemo } from "react";
import { Table, Nav, NavItem, NavLink } from "react-bootstrap";
import cn from "classnames";
import moment from "moment";
import "moment/locale/ru";

import { SwitchAnimation } from "../../Animation/Animation.jsx";

import { ScheduleButtonMobile } from "../ScheduleButtonMobile/ScheduleButtonMobile.jsx";
import { ScheduleButton } from "../ScheduleButton/ScheduleButton.jsx";

import styles from "./ScheduleTable.module.css";

export const ScheduleTable = ({ date, data, module, setDate, isForming }) => {
  const compareTime = (lessonDate, tableDate) => {
    const timeFormat = "HH:mm";
    const lessonTime = lessonDate.split("T")[1];
    const tableTime = tableDate.format(timeFormat);
    const tableRangeTime = tableDate.clone().add(30, "m").format(timeFormat);
    return moment(lessonTime, timeFormat).isBetween(
      moment(tableTime, timeFormat),
      moment(tableRangeTime, timeFormat),
      "minute",
      "[)"
    );
  };

  const compareDate = (lessonDate, tableDate) => {
    const tableRangeDate = tableDate.clone().add(30, "m");
    return moment(lessonDate).isBetween(
      tableDate,
      tableRangeDate,
      "minute",
      "[)"
    );
  };

  const sortedLessonsByDay = useMemo(() => {
    return date.dateArray.map((day) => {
      return data.filter((lesson) => {
        if (isForming || lesson.type?.includes("forming")) {
          return lesson.weekday === day.isoWeekday();
        } else {
          return day.isSame(lesson.date, "day");
        }
      });
    });
  }, [data, date.dateArray, isForming]);

  const sortedLessonsByTime = useMemo(() => {
    const startOfDay = date.today.clone().set({ hour: 8, minute: 30 });
    const endOfDay = date.today.clone().set({ hour: 19, minute: 0 });
    let time = startOfDay;
    const result = [];

    while (time <= endOfDay) {
      const rowOfTable = { [time.format("HH:mm")]: [] };
      for (const day in date.dateArray) {
        const fullTime = date.dateArray[day]
          .clone()
          .set({ hour: time.hour(), minute: time.minute() });
        const lessonFilter = data.filter((lesson) => {
          if (isForming || lesson.type?.includes("forming")) {
            return (
              fullTime.isoWeekday() === lesson.weekday &&
              compareTime(lesson.date, fullTime)
            );
          } else {
            return compareDate(lesson.date, fullTime);
          }
        });
        rowOfTable[time.format("HH:mm")].push(lessonFilter);
      }
      const checkOfBlank = Object.values(rowOfTable)[0].some((item) => {
        return item.length;
      });
      time = time.clone().add(30, "m");
      if (checkOfBlank) {
        result.push(rowOfTable);
      }
    }

    return result;
  }, [isForming, data, date.today, date.dateArray]);

  const filtredLessonsByTime = useMemo(() => {
    if (module) {
      return sortedLessonsByTime
        .map((row) => {
          return {
            [Object.keys(row)]: Object.values(row)[0].map((column) => {
              return column.filter((lesson) => {
                return lesson.course.module.name.includes(module);
              });
            }),
          };
        })
        .filter((row) => {
          return Object.values(row)[0].some((item) => {
            return item.length;
          });
        });
    } else {
      return sortedLessonsByTime;
    }
  }, [module, sortedLessonsByTime]);

  const filtredLessonsByDay = useMemo(() => {
    if (module) {
      return sortedLessonsByDay.map((dayArr) => {
        if (dayArr.length) {
          return dayArr.filter((lesson) => {
            return lesson.course.module.name.includes(module);
          });
        } else {
          return dayArr;
        }
      });
    } else {
      return sortedLessonsByDay;
    }
  }, [module, sortedLessonsByDay]);

  return (
    <div>
      <Table
        hover
        className={cn(styles.scheduleTable, "mt-3", "d-none", "d-md-table")}
      >
        <thead>
          <tr>
            <th>
              <i className="fa fa-clock-o" />
            </th>
            {date.dateArray.map((day, index) => {
              if (isForming) {
                return <th key={index}>{day.format("dddd")}</th>;
              } else {
                return <th key={index}>{day.format("dd. D.MM")}</th>;
              }
            })}
          </tr>
        </thead>
        <tbody>
          {filtredLessonsByTime.length ? (
            filtredLessonsByTime.map((row, index) => {
              return (
                <tr key={index}>
                  <td>{Object.keys(row)}</td>
                  {Object.values(row).map((column) => {
                    return column.map((lessons, index) => {
                      return (
                        <td key={index}>
                          {lessons.map((lesson, index) => {
                            if (!lesson) return null;
                            const forming = lesson.type?.includes("forming");
                            return (
                              <ScheduleButton
                                key={index}
                                data={lesson}
                                forming={forming}
                              />
                            );
                          })}
                        </td>
                      );
                    });
                  })}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={8}>
                <h1 className="text-center my-3">Выбранный день пуст</h1>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <div className="d-block d-md-none mt-3">
        <Nav className={styles.mobileHeader} variant="pills nav-fill">
          {date.dateArray.map((day, index) => {
            return (
              <NavItem key={index}>
                <NavLink
                  className={cn("m-1", "p-1", {
                    active: (() => {
                      const d = day.day() === 0 ? 7 : day.day();
                      return date.activeDay === d;
                    })(),
                  })}
                  onClick={() =>
                    day.day() === 0
                      ? setDate({ ...date, activeDay: 7 })
                      : setDate({ ...date, activeDay: day.day() })
                  }
                >
                  {day.format("ddd")}
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>
        <SwitchAnimation keyAnimation={date.activeDay}>
          <div className={cn("col", "my-3", styles.mobileLessonList)}>
            {filtredLessonsByDay.map((lessonArrayByDay, index) => {
              if (index + 1 === date.activeDay) {
                if (lessonArrayByDay.length) {
                  return lessonArrayByDay.map((lesson, index) => {
                    return (
                      <ScheduleButtonMobile
                        key={index}
                        data={lesson}
                        forming={lesson.type === "forming"}
                      />
                    );
                  });
                } else {
                  return (
                    <h2 className="text-center my-3" key={index}>
                      Выбранный день пуст
                    </h2>
                  );
                }
              } else {
                return null;
              }
            })}
          </div>
        </SwitchAnimation>
      </div>
    </div>
  );
};
